import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEye, FiVolume2, FiChevronUp, FiSettings, FiX, FiMousePointer, FiPause, FiUser, FiZap, FiMoon, FiSun, FiPlay, FiStop } = FiIcons;

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [activeProfile, setActiveProfile] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [speechVolume, setSpeechVolume] = useState(1);
  const speechSynthRef = useRef(null);
  const currentUtteranceRef = useRef(null);
  const [settings, setSettings] = useState({
    screenReader: false,
    highContrast: false,
    darkMode: false,
    reducedMotion: false,
    largeText: false,
    cursorSize: 'normal',
    keyboardNavigation: false
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Apply settings to document
    applyAccessibilitySettings();
  }, [settings, activeProfile]);

  useEffect(() => {
    // Initialize speech synthesis
    if ('speechSynthesis' in window) {
      speechSynthRef.current = window.speechSynthesis;
    }

    return () => {
      // Cleanup speech synthesis
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const speak = (text, options = {}) => {
    if (!speechSynthRef.current || !settings.screenReader) return;

    // Cancel any ongoing speech
    speechSynthRef.current.cancel();

    if (text && text.trim()) {
      const utterance = new SpeechSynthesisUtterance(text.trim());
      utterance.rate = options.rate || speechRate;
      utterance.volume = options.volume || speechVolume;
      utterance.pitch = options.pitch || 1;

      utterance.onstart = () => {
        setIsSpeaking(true);
        currentUtteranceRef.current = utterance;
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        currentUtteranceRef.current = null;
      };

      utterance.onerror = () => {
        setIsSpeaking(false);
        currentUtteranceRef.current = null;
      };

      speechSynthRef.current.speak(utterance);
    }
  };

  const stopSpeaking = () => {
    if (speechSynthRef.current) {
      speechSynthRef.current.cancel();
      setIsSpeaking(false);
      currentUtteranceRef.current = null;
    }
  };

  const applyAccessibilitySettings = () => {
    const root = document.documentElement;
    const body = document.body;

    // High Contrast
    if (settings.highContrast) {
      body.classList.add('high-contrast');
    } else {
      body.classList.remove('high-contrast');
    }

    // Dark Mode
    if (settings.darkMode) {
      body.classList.add('dark-mode');
    } else {
      body.classList.remove('dark-mode');
    }

    // Reduced Motion
    if (settings.reducedMotion) {
      root.style.setProperty('--motion-reduce', 'none');
      body.classList.add('reduced-motion');
    } else {
      root.style.setProperty('--motion-reduce', '');
      body.classList.remove('reduced-motion');
    }

    // Large Text
    if (settings.largeText) {
      root.style.fontSize = '20px';
      body.classList.add('large-text');
    } else {
      root.style.fontSize = '16px';
      body.classList.remove('large-text');
    }

    // Cursor Size
    const cursorClasses = ['cursor-small', 'cursor-normal', 'cursor-large'];
    cursorClasses.forEach(cls => body.classList.remove(cls));
    body.classList.add(`cursor-${settings.cursorSize}`);

    // Keyboard Navigation
    if (settings.keyboardNavigation) {
      body.classList.add('keyboard-navigation');
      addKeyboardNavigation();
    } else {
      body.classList.remove('keyboard-navigation');
    }

    // Screen Reader
    if (settings.screenReader) {
      enableScreenReader();
    } else {
      disableScreenReader();
    }
  };

  const enableScreenReader = () => {
    // Add screen reader functionality
    addScreenReaderListeners();
    enhanceAriaLabels();
    addReadingHighlight();
    speak("Screen reader mode activated. I will now read content as you navigate. Use Tab to move between elements, Enter to activate, and Escape to close menus.");
  };

  const disableScreenReader = () => {
    // Remove screen reader functionality
    removeScreenReaderListeners();
    removeReadingHighlight();
    stopSpeaking();
  };

  const addScreenReaderListeners = () => {
    // Mouse hover reading
    const handleMouseOver = (e) => {
      if (!settings.screenReader) return;
      
      const element = e.target;
      const text = getElementText(element);
      if (text) {
        speak(text);
        highlightElement(element);
      }
    };

    // Focus reading
    const handleFocus = (e) => {
      if (!settings.screenReader) return;
      
      const element = e.target;
      const text = getElementText(element);
      if (text) {
        speak(text);
        highlightElement(element);
      }
    };

    // Click reading
    const handleClick = (e) => {
      if (!settings.screenReader) return;
      
      const element = e.target;
      const text = getElementText(element);
      if (text) {
        speak(`Clicked: ${text}`);
      }
    };

    // Keyboard navigation
    const handleKeyDown = (e) => {
      if (!settings.screenReader) return;

      if (e.key === 'Tab') {
        setTimeout(() => {
          const activeElement = document.activeElement;
          if (activeElement) {
            const text = getElementText(activeElement);
            if (text) {
              speak(text);
              highlightElement(activeElement);
            }
          }
        }, 100);
      }

      if (e.key === 'Escape') {
        stopSpeaking();
        setIsOpen(false);
      }

      if (e.key === ' ' && e.ctrlKey) {
        e.preventDefault();
        if (isSpeaking) {
          stopSpeaking();
        } else {
          const text = getPageContent();
          if (text) speak(text);
        }
      }
    };

    // Add event listeners
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    // Store references for cleanup
    window.screenReaderListeners = {
      mouseover: handleMouseOver,
      focus: handleFocus,
      click: handleClick,
      keydown: handleKeyDown
    };
  };

  const removeScreenReaderListeners = () => {
    if (window.screenReaderListeners) {
      document.removeEventListener('mouseover', window.screenReaderListeners.mouseover);
      document.removeEventListener('focus', window.screenReaderListeners.focus, true);
      document.removeEventListener('click', window.screenReaderListeners.click);
      document.removeEventListener('keydown', window.screenReaderListeners.keydown);
      delete window.screenReaderListeners;
    }
  };

  const getElementText = (element) => {
    if (!element) return '';

    // Check for aria-label first
    if (element.getAttribute('aria-label')) {
      return element.getAttribute('aria-label');
    }

    // Check for title attribute
    if (element.getAttribute('title')) {
      return element.getAttribute('title');
    }

    // Check for alt text on images
    if (element.tagName === 'IMG' && element.getAttribute('alt')) {
      return `Image: ${element.getAttribute('alt')}`;
    }

    // Check for input labels
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      const label = document.querySelector(`label[for="${element.id}"]`);
      if (label) {
        return `${label.textContent.trim()}: ${element.value || 'empty field'}`;
      }
      return `${element.type || 'input'}: ${element.value || 'empty field'}`;
    }

    // Check for button text
    if (element.tagName === 'BUTTON') {
      return `Button: ${element.textContent.trim()}`;
    }

    // Check for link text
    if (element.tagName === 'A') {
      return `Link: ${element.textContent.trim()}`;
    }

    // Check for headings
    if (element.tagName.match(/^H[1-6]$/)) {
      return `Heading ${element.tagName.slice(1)}: ${element.textContent.trim()}`;
    }

    // Get text content
    const text = element.textContent?.trim();
    if (text && text.length > 0 && text.length < 200) {
      return text;
    }

    return '';
  };

  const getPageContent = () => {
    const main = document.querySelector('main') || document.body;
    const headings = main.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const paragraphs = main.querySelectorAll('p');
    
    let content = '';
    
    headings.forEach((heading, index) => {
      if (index < 5) { // Limit to first 5 headings
        content += `${heading.textContent.trim()}. `;
      }
    });
    
    paragraphs.forEach((p, index) => {
      if (index < 3) { // Limit to first 3 paragraphs
        const text = p.textContent.trim();
        if (text.length > 0 && text.length < 300) {
          content += `${text}. `;
        }
      }
    });
    
    return content;
  };

  const highlightElement = (element) => {
    // Remove existing highlights
    const existingHighlights = document.querySelectorAll('.screen-reader-highlight');
    existingHighlights.forEach(el => el.classList.remove('screen-reader-highlight'));

    // Add highlight to current element
    if (element) {
      element.classList.add('screen-reader-highlight');
      
      // Remove highlight after 3 seconds
      setTimeout(() => {
        element.classList.remove('screen-reader-highlight');
      }, 3000);
    }
  };

  const addReadingHighlight = () => {
    const style = document.createElement('style');
    style.id = 'screen-reader-highlight-style';
    style.textContent = `
      .screen-reader-highlight {
        outline: 3px solid #fbbf24 !important;
        outline-offset: 2px !important;
        background-color: rgba(251, 191, 36, 0.1) !important;
        border-radius: 4px !important;
        position: relative !important;
        z-index: 1000 !important;
      }
      
      .screen-reader-highlight::before {
        content: "🔊";
        position: absolute;
        top: -25px;
        left: 0;
        background: #fbbf24;
        color: black;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 12px;
        z-index: 1001;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  };

  const removeReadingHighlight = () => {
    const style = document.getElementById('screen-reader-highlight-style');
    if (style) {
      style.remove();
    }
    
    const highlights = document.querySelectorAll('.screen-reader-highlight');
    highlights.forEach(el => el.classList.remove('screen-reader-highlight'));
  };

  const addKeyboardNavigation = () => {
    // Enhanced keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-focus');
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    });

    document.addEventListener('mousedown', () => {
      document.body.classList.remove('keyboard-focus');
    });
  };

  const toggleScreenReader = () => {
    const newState = !settings.screenReader;
    setSettings(prev => ({ ...prev, screenReader: newState }));
    
    if (newState) {
      speak("Screen reader activated. I will now read content as you navigate and interact with elements.");
    } else {
      speak("Screen reader deactivated.");
      setTimeout(() => stopSpeaking(), 1000);
    }
  };

  const enhanceAriaLabels = () => {
    // Add enhanced ARIA labels to images without alt text
    const images = document.querySelectorAll('img:not([alt])');
    images.forEach((img, index) => {
      img.setAttribute('alt', `Image ${index + 1}: Decorative content`);
    });

    // Add role descriptions to interactive elements
    const buttons = document.querySelectorAll('button:not([aria-label])');
    buttons.forEach(button => {
      const text = button.textContent.trim();
      if (text) {
        button.setAttribute('aria-label', `Button: ${text}`);
      }
    });

    // Add labels to form elements
    const inputs = document.querySelectorAll('input:not([aria-label]):not([aria-labelledby])');
    inputs.forEach(input => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      if (!label && input.placeholder) {
        input.setAttribute('aria-label', input.placeholder);
      }
    });
  };

  const toggleHighContrast = () => {
    setSettings(prev => ({ ...prev, highContrast: !prev.highContrast }));
    const message = 'High contrast mode ' + (!settings.highContrast ? 'enabled' : 'disabled');
    announceChange(message);
    if (settings.screenReader) speak(message);
  };

  const toggleDarkMode = () => {
    setSettings(prev => ({ ...prev, darkMode: !prev.darkMode }));
    const message = 'Dark mode ' + (!settings.darkMode ? 'enabled' : 'disabled');
    announceChange(message);
    if (settings.screenReader) speak(message);
  };

  const toggleReducedMotion = () => {
    setSettings(prev => ({ ...prev, reducedMotion: !prev.reducedMotion }));
    const message = 'Reduced motion ' + (!settings.reducedMotion ? 'enabled' : 'disabled');
    announceChange(message);
    if (settings.screenReader) speak(message);
  };

  const toggleLargeText = () => {
    setSettings(prev => ({ ...prev, largeText: !prev.largeText }));
    const message = 'Large text ' + (!settings.largeText ? 'enabled' : 'disabled');
    announceChange(message);
    if (settings.screenReader) speak(message);
  };

  const changeCursorSize = (size) => {
    setSettings(prev => ({ ...prev, cursorSize: size }));
    const message = `Cursor size changed to ${size}`;
    announceChange(message);
    if (settings.screenReader) speak(message);
  };

  const toggleKeyboardNavigation = () => {
    setSettings(prev => ({ ...prev, keyboardNavigation: !prev.keyboardNavigation }));
    const message = 'Enhanced keyboard navigation ' + (!settings.keyboardNavigation ? 'enabled' : 'disabled');
    announceChange(message);
    if (settings.screenReader) speak(message);
  };

  const announceChange = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  };

  const disabilityProfiles = [
    {
      id: 'visual',
      name: 'Visual Impairments',
      icon: FiEye,
      description: 'Optimized for screen readers and low vision',
      settings: { screenReader: true, highContrast: true, largeText: true, keyboardNavigation: true }
    },
    {
      id: 'motor',
      name: 'Motor Impairments',
      icon: FiMousePointer,
      description: 'Enhanced keyboard navigation and larger click targets',
      settings: { keyboardNavigation: true, cursorSize: 'large', reducedMotion: true }
    },
    {
      id: 'cognitive',
      name: 'Cognitive Disabilities',
      icon: FiUser,
      description: 'Simplified interface with reduced distractions',
      settings: { reducedMotion: true, largeText: true, highContrast: true }
    },
    {
      id: 'epilepsy',
      name: 'Epilepsy Safe',
      icon: FiPause,
      description: 'Stops flashing and reduces motion',
      settings: { reducedMotion: true, highContrast: false }
    },
    {
      id: 'adhd',
      name: 'ADHD Friendly',
      icon: FiZap,
      description: 'Minimizes distractions and enhances focus',
      settings: { reducedMotion: true, darkMode: true }
    }
  ];

  const applyProfile = (profile) => {
    setActiveProfile(profile.id);
    setSettings(prev => ({ ...prev, ...profile.settings }));
    const message = `${profile.name} profile activated`;
    announceChange(message);
    if (settings.screenReader) speak(message);
  };

  const resetSettings = () => {
    setSettings({
      screenReader: false,
      highContrast: false,
      darkMode: false,
      reducedMotion: false,
      largeText: false,
      cursorSize: 'normal',
      keyboardNavigation: false
    });
    setActiveProfile(null);
    stopSpeaking();
    const message = 'All accessibility settings reset';
    announceChange(message);
  };

  return (
    <>
      {/* Accessibility Widget */}
      <div className="fixed left-4 top-1/2 transform -translate-y-1/2 z-50">
        <motion.div
          initial={{ x: -100 }}
          animate={{ x: 0 }}
          className="bg-white rounded-lg shadow-xl border border-slate-200"
        >
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className="w-12 h-12 bg-emerald-600 text-white rounded-lg flex items-center justify-center hover:bg-emerald-700 transition-colors duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label={isOpen ? "Close accessibility options" : "Open accessibility options"}
            aria-expanded={isOpen}
          >
            <SafeIcon icon={isOpen ? FiX : FiSettings} className="w-5 h-5" />
          </motion.button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute left-0 top-14 bg-white rounded-lg shadow-xl border border-slate-200 p-4 w-80 max-h-96 overflow-y-auto"
                role="dialog"
                aria-labelledby="accessibility-title"
              >
                <h3 id="accessibility-title" className="text-lg font-semibold text-slate-900 mb-4">
                  Accessibility Options
                </h3>
                
                {/* Screen Reader Controls */}
                {settings.screenReader && (
                  <div className="mb-4 p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-emerald-800">Screen Reader Active</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={stopSpeaking}
                          className="p-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                          aria-label="Stop speaking"
                        >
                          <SafeIcon icon={FiStop} className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-emerald-700">
                      Hover or focus on elements to hear them read aloud. Press Ctrl+Space to read page content.
                    </div>
                  </div>
                )}
                
                {/* Disability Profiles */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Disability Profiles</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {disabilityProfiles.map((profile) => (
                      <button
                        key={profile.id}
                        onClick={() => applyProfile(profile)}
                        className={`flex items-center p-2 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200 ${
                          activeProfile === profile.id ? 'bg-emerald-50 border border-emerald-200' : ''
                        }`}
                        aria-label={`${profile.name}: ${profile.description}`}
                      >
                        <SafeIcon icon={profile.icon} className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{profile.name}</div>
                          <div className="text-xs text-slate-600">{profile.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Individual Settings */}
                <div className="space-y-3">
                  <div className="border-t border-slate-200 pt-3">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Individual Settings</h4>
                  </div>

                  <button
                    onClick={toggleScreenReader}
                    className={`w-full flex items-center p-3 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200 ${
                      settings.screenReader ? 'bg-emerald-50' : ''
                    }`}
                    aria-label="Toggle screen reader support"
                    aria-pressed={settings.screenReader}
                  >
                    <SafeIcon icon={FiVolume2} className="w-5 h-5 text-emerald-600 mr-3" />
                    <span className="text-slate-700">Screen Reader</span>
                    {settings.screenReader && <span className="ml-auto text-emerald-600 text-sm">ON</span>}
                  </button>

                  <button
                    onClick={toggleHighContrast}
                    className={`w-full flex items-center p-3 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200 ${
                      settings.highContrast ? 'bg-emerald-50' : ''
                    }`}
                    aria-label="Toggle high contrast mode"
                    aria-pressed={settings.highContrast}
                  >
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-emerald-600 mr-3" />
                    <span className="text-slate-700">High Contrast</span>
                    {settings.highContrast && <span className="ml-auto text-emerald-600 text-sm">ON</span>}
                  </button>

                  <button
                    onClick={toggleDarkMode}
                    className={`w-full flex items-center p-3 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200 ${
                      settings.darkMode ? 'bg-emerald-50' : ''
                    }`}
                    aria-label="Toggle dark mode"
                    aria-pressed={settings.darkMode}
                  >
                    <SafeIcon icon={settings.darkMode ? FiSun : FiMoon} className="w-5 h-5 text-emerald-600 mr-3" />
                    <span className="text-slate-700">Dark Mode</span>
                    {settings.darkMode && <span className="ml-auto text-emerald-600 text-sm">ON</span>}
                  </button>

                  <button
                    onClick={toggleReducedMotion}
                    className={`w-full flex items-center p-3 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200 ${
                      settings.reducedMotion ? 'bg-emerald-50' : ''
                    }`}
                    aria-label="Toggle reduced motion"
                    aria-pressed={settings.reducedMotion}
                  >
                    <SafeIcon icon={FiPause} className="w-5 h-5 text-emerald-600 mr-3" />
                    <span className="text-slate-700">Reduce Motion</span>
                    {settings.reducedMotion && <span className="ml-auto text-emerald-600 text-sm">ON</span>}
                  </button>

                  <button
                    onClick={toggleLargeText}
                    className={`w-full flex items-center p-3 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200 ${
                      settings.largeText ? 'bg-emerald-50' : ''
                    }`}
                    aria-label="Toggle large text"
                    aria-pressed={settings.largeText}
                  >
                    <span className="text-emerald-600 mr-3 font-bold">A</span>
                    <span className="text-slate-700">Large Text</span>
                    {settings.largeText && <span className="ml-auto text-emerald-600 text-sm">ON</span>}
                  </button>

                  <button
                    onClick={toggleKeyboardNavigation}
                    className={`w-full flex items-center p-3 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200 ${
                      settings.keyboardNavigation ? 'bg-emerald-50' : ''
                    }`}
                    aria-label="Toggle enhanced keyboard navigation"
                    aria-pressed={settings.keyboardNavigation}
                  >
                    <SafeIcon icon={FiMousePointer} className="w-5 h-5 text-emerald-600 mr-3" />
                    <span className="text-slate-700">Keyboard Navigation</span>
                    {settings.keyboardNavigation && <span className="ml-auto text-emerald-600 text-sm">ON</span>}
                  </button>

                  <div className="border-t border-slate-200 pt-3">
                    <p className="text-sm text-slate-600 mb-2">Cursor Size</p>
                    <div className="flex space-x-2">
                      {['small', 'normal', 'large'].map(size => (
                        <button
                          key={size}
                          onClick={() => changeCursorSize(size)}
                          className={`flex-1 px-3 py-2 rounded text-sm transition-colors duration-200 ${
                            settings.cursorSize === size 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 hover:bg-slate-200'
                          }`}
                          aria-label={`Set cursor size to ${size}`}
                        >
                          {size.charAt(0).toUpperCase() + size.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={resetSettings}
                    className="w-full mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors duration-200"
                    aria-label="Reset all accessibility settings"
                  >
                    Reset All Settings
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center hover:bg-emerald-700 transition-colors duration-200 shadow-lg z-40"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Back to top of page"
          >
            <SafeIcon icon={FiChevronUp} className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Screen Reader Only Content */}
      <div className="sr-only">
        <h1>Namvoro - Digital Brand Management</h1>
        <p>Navigate using tab key. Press enter to activate buttons and links. Press escape to close menus. Press Ctrl+Space to read page content.</p>
      </div>
    </>
  );
};

export default AccessibilityWidget;